import axiosInstance from "../lib/axios.instance";
import type {
  ReviewDefinitionsResponse,
  StoredResponseRequest,
  StoredResponseResult,
  GenerateReviewRequest,
  RemoveTileRequest,
  DismissAllTilesRequest,
  UpdateTileStatusRequest,
  EditTileRequest,
  EditTileResponse,
  BulkWriteItRequest,
  BulkWriteItResponse,
  WriteItRequest,
} from "../types/review.types";

const BASE_URL = "/ai";

/**
 * Fetch available review definitions from the API
 */
export const getDefinitions = async (): Promise<ReviewDefinitionsResponse> => {
  const response = await axiosInstance.get<ReviewDefinitionsResponse>(`${BASE_URL}/definitions/`);
  return response.data;
};

/**
 * Generate review with query terms
 */
export const generateReview = async (
  request: GenerateReviewRequest,
  signal?: AbortSignal
): Promise<StoredResponseResult> => {
  const { query_term, ...payload } = request;
  const response = await axiosInstance.post<StoredResponseResult>(
    `${BASE_URL}/document/query/${query_term}`,
    payload,
    { signal }
  );
  return response.data;
};

/**
 * Get stored response for document query
 */
export const getStoredResponse = async (
  request: StoredResponseRequest
): Promise<StoredResponseResult> => {
  const response = await axiosInstance.post<StoredResponseResult>(
    `${BASE_URL}/document/query/response/stored-response`,
    request
  );
  return response.data;
};

/**
 * Format option key into readable text
 */
const formatOptionText = (key: string): string => {
  return key
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Remove tile from document query response
 */
export const removeTile = async (request: RemoveTileRequest): Promise<void> => {
  await axiosInstance.delete(`${BASE_URL}/document/query/response/remove-tile`, {
    data: request,
  });
};

/**
 * Dismiss all tiles for a document and query terms.
 */
export const dismissAllTiles = async (request: DismissAllTilesRequest): Promise<void> => {
  await axiosInstance.delete(`${BASE_URL}/document/query/response/remove-document-responses`, {
    data: request,
  });
};

/**
 * Process review definitions into structured options
 */
export const processDefinitions = (definitions: ReviewDefinitionsResponse) => {
  const options: Array<{ key: string; text: string; category: string }> = [];

  // Process regular reviews, filtering out compare-precedent
  definitions.reviews.forEach((item) => {
    if (typeof item === "string") {
      options.push({
        key: item,
        text: formatOptionText(item),
        category: "review",
      });
    }
    // Skip compare-precedent options as per requirements
  });

  return options;
};

/**
 * Update the status of a tile
 */
export const updateTileStatus = async (data: UpdateTileStatusRequest): Promise<void> => {
  await axiosInstance.post(`/ai/document/query/response/update-status`, data);
};

/**
 * Edit a tile
 */
export const editTile = async (request: EditTileRequest): Promise<EditTileResponse> => {
  const response = await axiosInstance.put<EditTileResponse>(
    `${BASE_URL}/document/query/response/edit-tile`,
    request
  );
  return response.data;
};

/**
 * Bulk write it for multiple tiles
 */
export const bulkWriteIt = async (request: BulkWriteItRequest): Promise<BulkWriteItResponse> => {
  const response = await axiosInstance.post<BulkWriteItResponse>(
    `${BASE_URL}/document/wifm/`,
    request
  );
  return response.data;
};

/**
 * Write it for me for a single tile
 */
export const writeIt = async (request: WriteItRequest): Promise<BulkWriteItResponse> => {
  const response = await axiosInstance.post<BulkWriteItResponse>(
    `${BASE_URL}/document/wifm/${request.query_term}`,
    request
  );
  return response.data;
};
